import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { generateReport } from "../../api/report";
import {
  GENERATE_REPORT_MESSAGE,
  YOUR_REPORT_FAILED_TO_GENERATE_PLEASE_TRY_AGAIN_MESSAGE,
  YOUR_REPORT_HAS_BEEN_GENERATED_MESSAGE,
} from "../../util/messageConstants";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    fontWeight: "bold",
    marginLeft: "15px",
  },
  button: {
    margin: "5px",
    width: "100px",
  },
  link: {
    display: "none",
  },
  warningBox: {
    marginTop: "10px",
    width: "100%",
  },
  selectDropdown: {
    width: "100%",
  },
}));

const GenerateReportsDialog = (props) => {
  const classes = useStyles();
  const [reportType, setReportType] = useState("");
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [isReportDownloadError, setIsReportDownloadError] = useState(false);
  const [isReportDownloadComplete, setIsReportDownloadComplete] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const linkRef = useRef();

  const resetState = () => {
    setIsReportDownloading(false);
    setIsReportDownloadError(false);
    setIsReportDownloadComplete(false);
    setResponseMessage("");
  };

  const handleCancelButtonClick = () => {
    props.setIsDialogOpen(false);
  };

  const handleGenerateReport = async () => {
    resetState();
    try {
      setIsReportDownloading(true);
      let res = await generateReport(reportType);
      res = await res.json();

      if (res.message) {
        setResponseMessage(res.message);
        setIsReportDownloading(false);
      } else if (!res.error) {
        const a = linkRef.current;
        a.href = res.downloadUrl;
        a.click();
        a.href = "";
        setIsReportDownloading(false);
        setIsReportDownloadComplete(true);
      } else {
        setIsReportDownloading(false);
        setIsReportDownloadError(true);
        console.error(`Error while downloading ${reportType}`);
      }
    } catch (error) {
      setIsReportDownloading(false);
      setIsReportDownloadError(true);
      console.error(`Error while downloading ${reportType}: ${error}`);
    }
  };

  const renderReportTypes = () => {
    return props.reports
      .slice()
      .sort()
      .map((reportType) => {
        return (
          <MenuItem key={reportType} value={reportType}>
            {reportType}
          </MenuItem>
        );
      });
  };

  const handleReportTypeChange = (event) => {
    resetState();
    setReportType(event.target.value);
  };

  return (
    <>
      <Dialog
        className="detail-dialog"
        data-testid="generate-reports-dialog"
        fullWidth
        fullScreen={!props.isDesktopView}
        maxWidth="xs"
        open={props.isDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
        onClose={handleCancelButtonClick}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle className={classes.dialogTitle} id="alert-dialog-slide-title">
          {GENERATE_REPORT_MESSAGE}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"} id="alert-dialog-slide-description">
            <Box mt={1} mr={2} ml={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Report Type</InputLabel>
                <Select
                  id="reportFilterSelectElement"
                  className={classes.selectDropdown}
                  label="Report Type"
                  value={reportType}
                  disabled={isReportDownloading}
                  onChange={handleReportTypeChange}
                >
                  <MenuItem disabled>
                    <em>Select Report Type</em>
                  </MenuItem>
                  {renderReportTypes()}
                </Select>
              </FormControl>
              {isReportDownloadError || responseMessage.length || isReportDownloadComplete ? (
                <div className={classes.warningBox}>
                  <Grid container justifyContent="start">
                    <Box>
                      <Alert
                        severity={responseMessage.length ? "warning" : isReportDownloadError ? "error" : "success"}
                      >
                        {responseMessage.length
                          ? responseMessage
                          : isReportDownloadError
                          ? YOUR_REPORT_FAILED_TO_GENERATE_PLEASE_TRY_AGAIN_MESSAGE
                          : YOUR_REPORT_HAS_BEEN_GENERATED_MESSAGE}
                      </Alert>
                    </Box>
                  </Grid>
                </div>
              ) : (
                ""
              )}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div>
            <Button className={classes.button} variant="outlined" color="primary" onClick={handleCancelButtonClick}>
              CANCEL
            </Button>
            {isReportDownloading ? (
              <Button className={classes.button} variant="contained" color="primary" disabled={true}>
                <CircularProgress size={24} />
              </Button>
            ) : (
              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                onClick={handleGenerateReport}
                disabled={reportType.length === 0}
              >
                Generate
              </Button>
            )}
            <a className={classes.link} ref={linkRef} href={"/"}>
              Generate Report
            </a>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

GenerateReportsDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  setIsDialogOpen: PropTypes.func.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  reports: PropTypes.array.isRequired,
};

export default GenerateReportsDialog;
